import { dehydrate, QueryClient } from '@tanstack/react-query';
import { getLocaleCodeByCountryCode, SUPPORTED_LOCALES_OBJ, type SupportedLocale } from '@zep-us/i18n';
import { type GetServerSidePropsContext } from 'next';
import { useQuizDetail } from '@/api/quiz';
import { getBaseUrlWithLocale } from '@/consts/env';
import { withSsrLogging } from '@/shared/logger/withSsrLogging';
import { logGooglebotDebug } from '@/utils/common';
import { getDefaultServerSideProps } from '@/utils/next';
import { handleIfModifiedSince, setNotFoundPageCache, setPublicPageCache, setRedirectPageCache } from '@/utils/ssr';
import { computeQuizLastModified } from './utils/computeQuizLastModified';
import { isQuizDetailIndexable } from './utils/isQuizDetailIndexable';
import { makeQuizDetailSeoInfo } from './utils/makeQuizDetailSeoInfo';
import { isConfirmedQuizNotFound, isExplicitNotFound } from './utils/quizDetailNotFound';
import { sendGoneToBot } from './utils/sendGoneToBot';
import { QuizDetailPageView } from './QuizDetailPageView';
export default QuizDetailPageView;
const logGooglebotQuizSsrDebug = ({
  context,
  event,
  quizId,
  locale,
  mappedLocale,
  statusCode,
  extra
}: {
  context: GetServerSidePropsContext;
  event: 'canonical-redirect' | 'conditional-result';
  quizId: string;
  locale: string;
  mappedLocale: string;
  statusCode: number;
  extra?: Record<string, unknown>;
}) => {
  if (context.req.headers['x-client-type'] !== 'bot') return;
  logGooglebotDebug({
    event,
    runtime: 'nodejs',
    routeKind: 'quiz-detail',
    quizId,
    locale,
    mappedLocale,
    requestUrl: context.req.url,
    statusCode,
    referer: context.req.headers.referer ?? null,
    'if-modified-since': context.req.headers['if-modified-since'] ?? null,
    'if-none-match': context.req.headers['if-none-match'] ?? null,
    'user-agent': context.req.headers['user-agent'] ?? null,
    ...extra
  });
};
export const getServerSideProps = withSsrLogging('quiz-detail', async function getServerSidePropsInner(context: GetServerSidePropsContext) {
  const {
    locale = SUPPORTED_LOCALES_OBJ.en,
    params
  } = context;
  const queryClient = new QueryClient();
  const quizId = params?.id as string ?? '';

  // prefetchQuery 는 silent fail (undefined) → 백엔드 장애가 그대로 false 404
  // 로 엣지에 캐시되는 사고가 발생. fetchQuery 로 바꾸되, throw 된 에러를
  // "명시적 not-found" 와 "그 외 장애" 로 구분한다.
  //   - 명시적 not-found(404/FAILED) → setNotFoundPageCache + notFound:true (캐시 OK)
  //   - 그 외 장애(5xx/timeout/네트워크) → rethrow → Next.js 500 (캐시 안 됨)
  // 410 은 확인된 HTTP 404 에만 준다. 본문 status:'FAILED' 는 403·500 에도
  // 실릴 수 있어(서버 CustomException 기본값) 장애를 영구 삭제로 굳힐 수 있다.
  const handleNotFound = ({
    gone
  }: {
    gone: boolean;
  }) => {
    const isBot = context.req.headers['x-client-type'] === 'bot';
    setNotFoundPageCache(context, {
      profile: isBot ? 'bot' : 'user'
    });
    // 봇에게는 404 대신 410 Gone. 404 는 크롤 큐에 오래 남는다.
    if (gone && sendGoneToBot(context)) return {
      props: {} as never
    };
    return {
      notFound: true as const
    };
  };

  // 두 호출의 throw 처리를 분리한다.
  //   - quizDetail throw → not-found vs 장애 분기
  //   - getDefaultServerSideProps throw → 그대로 rethrow (i18n/서버 장애)
  // 여기서 로그를 찍지 않는다 — rethrow 는 바깥 withSsrLogging 이 err 째로
  // 기록하며, 중복으로 찍으면 장애 1건이 2건으로 집계된다.
  const quizFetch = queryClient.fetchQuery(useQuizDetail.getFetchOptions({
    id: quizId
  })).catch((e: unknown) => {
    if (isExplicitNotFound(e)) {
      return {
        __notFound: true,
        gone: isConfirmedQuizNotFound(e)
      } as const;
    }
    throw e;
  });
  const [{
    props: defaultProps
  }, quizDetailOrNotFound] = await Promise.all([getDefaultServerSideProps(context), quizFetch]);
  if (quizDetailOrNotFound != null && '__notFound' in quizDetailOrNotFound && quizDetailOrNotFound.__notFound) {
    return handleNotFound({
      gone: quizDetailOrNotFound.gone
    });
  }
  const quizDetail = quizDetailOrNotFound as Exclude<typeof quizDetailOrNotFound, {
    __notFound: true;
  }>;

  // 200 OK 라도 응답 body 의 status 가 FAILED 면 not-found 처리. (httpClient
  // interceptor 가 잡지 못한 케이스 대비 안전망)
  // HTTP 200 인데 본문만 FAILED — 404 로 확인된 게 아니라 410 을 주지 않는다.
  if (quizDetail?.status === 'FAILED') return handleNotFound({
    gone: false
  });
  const mappedLocale = getLocaleCodeByCountryCode(quizDetail.data.countryCode);
  const canonicalUrl = `${getBaseUrlWithLocale(mappedLocale)}/quiz/${quizId}`;
  const ogUrl = `${getBaseUrlWithLocale(locale)}/quiz/${quizId}`;
  const seoContent = makeQuizDetailSeoInfo(quizDetail.data, locale as SupportedLocale, defaultProps._nextI18Next, canonicalUrl);
  const isBot = context.req.headers['x-client-type'] === 'bot';
  const shouldRedirect = isBot && locale !== mappedLocale;
  if (shouldRedirect) {
    // 색인 대상이 아닌 퀴즈의 다른 언어 주소는 리다이렉트해 봐야 noindex 페이지다.
    // 410 으로 구글이 옛 주소를 버리게 한다. 정식 주소는 이 분기를 타지 않는다.
    if (!isQuizDetailIndexable(quizDetail.data)) {
      setNotFoundPageCache(context, {
        profile: 'bot'
      });
      if (sendGoneToBot(context)) return {
        props: {} as never
      };
    }
    logGooglebotQuizSsrDebug({
      context,
      event: 'canonical-redirect',
      quizId,
      locale,
      mappedLocale,
      statusCode: 301,
      extra: {
        destination: canonicalUrl,
        quizUpdatedAt: quizDetail.data.updatedAt,
        isPublic: quizDetail.data.isPublic
      }
    });

    // 301 도 Edge 캐싱. 단 봇이 오는 주소가 거의 매번 달라 실제로 맞는 일은 드물다.
    setRedirectPageCache(context);
    return {
      redirect: {
        destination: canonicalUrl,
        statusCode: 301
      }
    };
  }

  // 공개 퀴즈만 Edge 캐싱 (비공개는 no-store)
  setPublicPageCache(context, {
    cacheable: quizDetail.data.isPublic,
    profile: isBot ? 'bot' : 'user'
  });

  // UA 별 엣지 캐시 버킷 분리. middleware 가 x-client-type(bot|user) 을 주입한다.
  // Vary 덕분에 엣지는 bot/user 두 버킷으로만 응답을 저장해 효율을 유지한다.
  context.res.setHeader('Vary', 'x-client-type');
  const lastModified = computeQuizLastModified({
    isBot,
    updatedAt: quizDetail.data.updatedAt
  });
  const conditionalMatched = handleIfModifiedSince(context, lastModified);
  logGooglebotQuizSsrDebug({
    context,
    event: 'conditional-result',
    quizId,
    locale,
    mappedLocale,
    statusCode: conditionalMatched ? 304 : 200,
    extra: {
      conditionalMatched,
      cacheProfile: isBot ? 'bot' : 'user',
      cacheControl: context.res.getHeader('Cache-Control') ?? null,
      vary: context.res.getHeader('Vary') ?? null,
      lastModified: context.res.getHeader('Last-Modified') ?? null,
      quizUpdatedAt: quizDetail.data.updatedAt,
      isPublic: quizDetail.data.isPublic
    }
  });
  if (conditionalMatched) {
    return {
      props: {} as never
    };
  }
  return {
    props: {
      ...defaultProps,
      quizId,
      dehydratedState: dehydrate(queryClient),
      seoContent,
      noIndex: !isQuizDetailIndexable(quizDetail.data),
      canonicalUrl,
      ogUrl
    }
  };
});